/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Divider, Stack, Button } from '@chakra-ui/core';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import {
  Candidate,
  InReviewQuestionListType,
  MultipleChoiceQuestionResponse,
  QuestionResponse,
} from '../../../../firebase/firestore/documents/candidate';
import { useStoreActions } from '../../../../models/hooks';
import { useTopCandidatesContext } from '../TopCandidatesContext';
import EngageCandidate from '../EngageCandidate';
import { QuestionFormdataType } from '../../../messaging/seeker/candidate/questions/QuestionType';
import { QuestionTypeT, QuestionOnReviewT, QuestionType, FormDataItem } from './ReviewQuestion.types';
import { QuestionItem } from './ReviewQuestionItem';

interface ReviewQuestionsProps {
  handleActionAfterEngage: (seekerId: string) => void;
  selectedCandidate?: Readonly<Candidate>;
  inReviewQuestionList?: InReviewQuestionListType;
  formDataSetterCallback?: (formData: QuestionFormdataType, isAllQuestionsReviewed: boolean) => void;
  renderedBy?: 'ReviewPausedQuestionModal';
  isCandidateMonetized?: boolean;
  givenPositionId?: string;
  givenSeekerId?: string;
  onCloseReviewModal?: () => void;
  updateTodoList?: () => void;
}

const isAnswerCorrect = (question: QuestionOnReviewT, type: QuestionTypeT | string): boolean => {
  switch (type) {
    case QuestionType.YES_NO: {
      const yesNoQuestion = question as QuestionResponse;
      return (
        (yesNoQuestion.preferredAnswer && yesNoQuestion.answer) ||
        (!yesNoQuestion.preferredAnswer && /* istanbul ignore next */ !yesNoQuestion.answer)
      );
    }
    case QuestionType.MCQ: {
      const mcqQuestion = question as MultipleChoiceQuestionResponse;
      return Boolean(mcqQuestion.isAnsweredCorrectly);
    }

    default:
      return false;
  }
};

const normalizeQuestionResponses = (questionList: InReviewQuestionListType): QuestionOnReviewT[] => {
  const questionResponses =
    questionList.questionResponses?.map((item) => ({
      ...item,
      type: QuestionType.YES_NO,
    })) ?? /* istanbul ignore next */ [];

  const freeFormQuestionResponses =
    questionList.freeFormQuestionResponses?.map((item) => ({
      ...item,
      type: QuestionType.FREE_FORM,
    })) ?? /* istanbul ignore next */ [];

  const multipleChoiceQuestionResponses =
    questionList.multipleChoiceQuestionResponses?.map((item) => ({
      text: item.question,
      ...item,
      type: QuestionType.MCQ,
    })) ?? /* istanbul ignore next */ [];

  const videoUploadQuestionResponses =
    questionList.videoUploadQuestionResponses?.map((item) => ({
      text: item.question,
      ...item,
      type: QuestionType.VIDEO_RESPONSE,
    })) ?? /* istanbul ignore next */ [];

  const documentUploadQuestionResponses =
    questionList.documentUploadQuestionResponses?.map((item) => ({
      text: item.question,
      ...item,
      type: QuestionType.DOCUMENT_UPLOAD,
    })) ?? /* istanbul ignore next */ [];

  return [
    ...questionResponses,
    ...freeFormQuestionResponses,
    ...multipleChoiceQuestionResponses,
    ...videoUploadQuestionResponses,
    ...documentUploadQuestionResponses,
  ] as QuestionOnReviewT[];
};

export const ReviewQuestions = React.memo(function ReviewQuestions({
  handleActionAfterEngage,
  selectedCandidate,
  inReviewQuestionList,
  formDataSetterCallback,
  renderedBy,
  isCandidateMonetized,
  givenPositionId,
  givenSeekerId,
  onCloseReviewModal,
  updateTodoList,
}: ReviewQuestionsProps) {
  const { t } = useTranslation();
  const portalRef = useRef<HTMLElement | null>(null);
  const { approveRejectQuestions } = useStoreActions((action) => action.positionManagement);
  const { positionDocument } = useTopCandidatesContext();

  const [formData, setFormData] = useState<FormDataItem[]>([]);
  const [isContinueLoading, setIsContinueLoading] = useState<boolean>(false);

  const isCurrentSelectedCandidateMonetized = useMemo(
    () => isCandidateMonetized || selectedCandidate?.monetized,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCandidateMonetized, selectedCandidate?.monetized],
  );

  const currentQuestionList = useMemo(
    () =>
      !isNil(inReviewQuestionList)
        ? inReviewQuestionList
        : {
            questionResponses: selectedCandidate?.questionResponses,
            freeFormQuestionResponses: selectedCandidate?.freeFormQuestionResponses,
            multipleChoiceQuestionResponses: selectedCandidate?.multipleChoiceQuestionResponses,
            videoUploadQuestionResponses: selectedCandidate?.videoUploadQuestionResponses,
            documentUploadQuestionResponses: selectedCandidate?.documentUploadQuestionResponses,
          },
    [inReviewQuestionList, selectedCandidate],
  );

  const questionsOnReview = useMemo(() => {
    // istanbul ignore next
    if (!selectedCandidate && isNil(inReviewQuestionList)) {
      return [];
    }

    const allQuestions = normalizeQuestionResponses(currentQuestionList);
    return allQuestions
      .filter((item) => !isAnswerCorrect(item, item.type))
      .filter((item) => item.isPausable)
      .filter((item) => isNil(item.reviewedBy))
      .filter((item) => !item.skipped);
  }, [selectedCandidate, inReviewQuestionList, currentQuestionList]);

  const isAllQuestionsReviewed = useMemo(() => {
    if (questionsOnReview.length === 0) return false;

    return questionsOnReview.every((question) => {
      const formDataItem = formData.find((item) => item.questionId === question.id);
      return Boolean(formDataItem) || Boolean(question.isAnswerAccepted);
    });
  }, [questionsOnReview, formData]);

  const candidateId = useMemo(
    () => {
      if (givenPositionId && givenSeekerId) {
        return `${givenPositionId}_${givenSeekerId}`;
      }
      return selectedCandidate?.id ?? /* istanbul ignore next */ '';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [givenPositionId, givenSeekerId, selectedCandidate?.id],
  );

  // Reset form data when candidate changes
  useEffect(
    () => {
      setFormData([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCandidate?.id],
  );

  useEffect(() => {
    const portalRoot = document.getElementById('topCandidateProfileAction');
    portalRef.current = portalRoot;
  }, []);

  useEffect(() => {
    formDataSetterCallback?.(formData, isAllQuestionsReviewed);
  }, [formData, formDataSetterCallback, isAllQuestionsReviewed]);

  const handleAnswerChange = useCallback((questionId: string, questionType: string, isAnswerAccepted: boolean) => {
    setFormData((prev) => {
      const existingIndex = prev.findIndex((item) => item.questionId === questionId);

      if (existingIndex === -1) {
        return [...prev, { questionId, questionType, isAnswerAccepted }];
      }

      const updated = [...prev];
      updated[existingIndex] = { ...updated[existingIndex], isAnswerAccepted };
      return updated;
    });
  }, []);

  const handleEngageCandidate = useCallback(async () => {
    // istanbul ignore next
    if (!candidateId) {
      console.error('Cannot engage candidate: missing candidate ID');
      return;
    }

    try {
      const response = await approveRejectQuestions({ candidateId, payload: formData });

      if (renderedBy === 'ReviewPausedQuestionModal' && onCloseReviewModal) {
        updateTodoList?.();
        onCloseReviewModal();
      }

      if (response && renderedBy !== 'ReviewPausedQuestionModal') {
        handleActionAfterEngage(candidateId.split('_')[1]);
      }
    } catch (error) {
      // istanbul ignore next
      console.error('Failed to approve/reject questions:', error);
      // istanbul ignore next
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, formData, approveRejectQuestions, renderedBy, onCloseReviewModal, updateTodoList]);

  const handleBeforeEngage = useCallback(
    async (before: () => Promise<void> | void) => {
      await handleEngageCandidate();
      await before();
    },
    [handleEngageCandidate],
  );

  // Early return if no questions to review
  if (questionsOnReview.length === 0) {
    return null;
  }

  return (
    <Box mt={4} data-testid="review-questions">
      <Stack divider={<Divider />} spacing={4}>
        {questionsOnReview.map((question) => (
          <QuestionItem key={question.id} question={question} formData={formData} onAnswerChange={handleAnswerChange} />
        ))}
      </Stack>
      {portalRef.current &&
        createPortal(
          <Box>
            {!isCurrentSelectedCandidateMonetized && (
              <EngageCandidate
                disabled={!isAllQuestionsReviewed}
                handleBeforeEngage={handleBeforeEngage}
                positionId={positionDocument?.id ?? /* istanbul ignore next */ ''}
                handleActionAfterEngage={handleActionAfterEngage}
                buttonSize={renderedBy === 'ReviewPausedQuestionModal' ? 'md' : 'sm'}
                givenPositionId={givenPositionId}
                givenSeekerId={givenSeekerId}
              />
            )}

            {isCurrentSelectedCandidateMonetized && renderedBy !== 'ReviewPausedQuestionModal' && (
              <Button
                fontSize={{ base: '9px', md: '11px', lg: '14px' }}
                isLoading={isContinueLoading}
                isDisabled={isContinueLoading || !isAllQuestionsReviewed}
                size="sm"
                data-testid="continueBtn"
                colorScheme="blue"
                onClick={() => {
                  setIsContinueLoading(true);
                  handleEngageCandidate().finally(() => setIsContinueLoading(false));
                }}
              >
                {t('businessManagement:topCandidate.continueBtnLabel')}
              </Button>
            )}
          </Box>,
          portalRef.current,
        )}
    </Box>
  );
});
